import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useLocation, useMatch} from 'react-router-dom';

import {always} from 'ramda';
import {isNotNilOrEmpty, isNotString} from 'ramda-adjunct';

import {vehiclesRoutes} from '@dms/routes';

import {composePath, useNavigate} from 'shared/web';

import {
  convertToSearchParams,
  getFormattedSourceSearchParams,
  getSourceSearchParams,
} from 'features/eag-grid';

import {queryParams} from '../utils/queryParams';
import {useNavigationBreadCrumb} from './useNavigationBreadCrumb';

export function useEntityNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [breadcrumbProps, isBreadcrumbVisible] = useNavigationBreadCrumb();
  const rowControlDetailMatch = useMatch(breadcrumbProps?.rowControlDetail ?? '');
  const isRowControlDetailVisible = Boolean(breadcrumbProps?.rowControlDetail);

  const handleBreadcrumb = () => {
    if (!breadcrumbProps) {
      showNotification.error();
      return;
    }

    if (isNotNilOrEmpty(getSourceSearchParams())) {
      const sourceGridCode = getFormattedSourceSearchParams().gridCode;

      const vehicleTab =
        breadcrumbProps.navigateTo === vehiclesRoutes.home
          ? match(sourceGridCode)
              .with('vehicles-in-advertisement', always('/in-advertisement'))
              .with('vehicle_in_warehouse_history', always('/warehouses'))
              .with('sale-vehicle-os', always('/sale-vehicle-opensearch'))
              .with('service-vehicle-os', always('/service-vehicle-opensearch'))
              .with('rental-vehicle-os', always('/rental-vehicles'))
              .otherwise(always(''))
          : '';

      const newSearchParams = convertToSearchParams(
        {
          [getFormattedSourceSearchParams().gridCode]: getFormattedSourceSearchParams(),
        },
        true
      );

      const sourcePage = new URLSearchParams(window.location.search).get('sourcePage');

      if (sourcePage !== null) {
        newSearchParams.page = `${sourceGridCode}:${sourcePage}`;
      }

      navigate(
        composePath(breadcrumbProps?.navigateTo + vehicleTab, {
          queryParams: newSearchParams,
          isSearchParamsPreserved: false,
        })
      );

      return;
    }

    navigate(breadcrumbProps?.navigateTo);
  };

  const handleNavigate = (
    newEntity: string,
    sourceSearchParams: string,
    additionalRouteParams?: Record<string, string>
  ) => {
    if (isNotString(breadcrumbProps?.rowControlDetail)) {
      throw Error('You need to specify rowControlDetail');
    }

    const URLSourceSearchParams = new URLSearchParams(sourceSearchParams);
    const locationSource = new URLSearchParams(location.search).get(queryParams.REDIRECT_SOURCE);
    if (locationSource) {
      URLSourceSearchParams.append(queryParams.REDIRECT_SOURCE, locationSource);
    }

    if (breadcrumbProps?.rowControlDetail) {
      const composedPath = composePath(breadcrumbProps.rowControlDetail as `${string}/:id`, {
        params: {
          ...rowControlDetailMatch?.params,
          id: newEntity,
          ...additionalRouteParams,
        },
        queryParams: URLSourceSearchParams.toString(),
        isSearchParamsPreserved: false,
      });

      navigate(composedPath);
    }
  };

  return {
    breadcrumbProps,
    isBreadcrumbVisible,
    rowControlDetailMatch,
    isRowControlDetailVisible,
    handleBreadcrumb,
    handleNavigate,
  };
}
